/**
 * LoginModal — connexion sociale Google / Facebook / Apple.
 * Charge les SDK OAuth dynamiquement (aucun package externe requis).
 * Affiche un message de limite atteinte si triggered depuis le chat.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, MessageCircle, Zap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || '';
const GOOGLE_CLIENT_ID  = process.env.REACT_APP_GOOGLE_CLIENT_ID  || '';
const FACEBOOK_APP_ID   = process.env.REACT_APP_FACEBOOK_APP_ID   || '';
const APPLE_CLIENT_ID   = process.env.REACT_APP_APPLE_CLIENT_ID   || '';

// ── Logos SVG ─────────────────────────────────────────────────────────────────
const GoogleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const AppleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

// ── Composant principal ───────────────────────────────────────────────────────
export default function LoginModal({ onClose, reason = 'login' }) {
  const { login } = useAuth();
  const [loading, setLoading] = useState(null);  // 'google' | 'facebook' | 'apple' | null
  const [error, setError]     = useState('');
  const googleBtnRef = useRef(null);
  const googleReady  = useRef(false);

  const isLimit = reason === 'limit';

  // ── Appel backend pour obtenir le JWT ───────────────────────────────────────
  const exchangeToken = async (provider, body) => {
    const res = await fetch(`${API_BASE}/api/auth/${provider}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Erreur ${res.status}`);
    }
    return res.json(); // { token, user }
  };

  // ── Google ──────────────────────────────────────────────────────────────────
  const initGoogle = () => {
    if (!window.google || !GOOGLE_CLIENT_ID || googleReady.current) return;
    googleReady.current = true;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        setLoading('google');
        setError('');
        try {
          const data = await exchangeToken('google', { id_token: response.credential });
          login(data.user, data.token);
          onClose();
        } catch (e) {
          setError(e.message);
        } finally {
          setLoading(null);
        }
      },
    });
    if (googleBtnRef.current) {
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_black',
        size: 'large',
        width: googleBtnRef.current.offsetWidth || 320,
        text: 'continue_with',
        shape: 'rectangular',
      });
    }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    if (window.google) { initGoogle(); return; }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.head.appendChild(script);
  }, []); // eslint-disable-line

  // ── Facebook ─────────────────────────────────────────────────────────────────
  const loginWithFacebook = () => {
    if (!window.FB) { setError('SDK Facebook non chargé. Rechargez la page.'); return; }
    setLoading('facebook');
    setError('');
    window.FB.login(async (response) => {
      if (!response.authResponse) {
        setLoading(null);
        setError('Connexion Facebook annulée.');
        return;
      }
      try {
        const data = await exchangeToken('facebook', {
          access_token: response.authResponse.accessToken,
        });
        login(data.user, data.token);
        onClose();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(null);
      }
    }, { scope: 'public_profile,email' });
  };

  useEffect(() => {
    if (!FACEBOOK_APP_ID || window.FB) return;
    window.fbAsyncInit = () => {
      window.FB.init({ appId: FACEBOOK_APP_ID, cookie: true, xfbml: true, version: 'v19.0' });
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/fr_FR/sdk.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []); // eslint-disable-line

  // ── Apple ────────────────────────────────────────────────────────────────────
  const loginWithApple = () => {
    if (!window.AppleID) { setError('SDK Apple non chargé. Rechargez la page.'); return; }
    setLoading('apple');
    setError('');
    window.AppleID.auth.signIn().then(async (response) => {
      try {
        const user  = response.user || {};
        const name  = user.name ? `${user.name.firstName || ''} ${user.name.lastName || ''}`.trim() : '';
        const data  = await exchangeToken('apple', {
          id_token: response.authorization.id_token,
          name,
        });
        login(data.user, data.token);
        onClose();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(null);
      }
    }).catch(() => {
      setLoading(null);
      setError('Connexion Apple annulée.');
    });
  };

  useEffect(() => {
    if (!APPLE_CLIENT_ID || window.AppleID) return;
    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => {
      window.AppleID?.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: window.location.origin,
        usePopup: true,
      });
    };
    document.head.appendChild(script);
  }, []); // eslint-disable-line

  // ── Rendu ─────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Carte */}
      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-primary/30
        bg-background/95 backdrop-blur-xl shadow-[0_0_80px_hsl(280_85%_58%/0.2)] p-6 space-y-5">

        {/* Fermer */}
        <button onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-white/5">
          <X className="w-4 h-4" />
        </button>

        {/* En-tête */}
        <div className="space-y-2 text-center pt-1">
          {isLimit ? (
            <>
              <div className="w-12 h-12 mx-auto rounded-full bg-primary/15 border border-primary/30
                flex items-center justify-center mb-3">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-lg font-bold text-foreground">Limite atteinte</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Vous avez utilisé vos <span className="text-primary font-semibold">10 messages gratuits</span>.
                Connectez-vous pour continuer sans limite.
              </p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-primary to-secondary
                flex items-center justify-center mb-3">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-lg font-bold text-foreground">Connexion</h2>
              <p className="text-sm text-muted-foreground">
                Accédez à l'expert Auth0 IA sans limite.
              </p>
            </>
          )}
        </div>

        {/* Boutons sociaux */}
        <div className="space-y-3">
          {/* Google — rendu par le SDK officiel */}
          {GOOGLE_CLIENT_ID ? (
            <div
              ref={googleBtnRef}
              className="w-full overflow-hidden rounded-xl"
              style={{ minHeight: 44 }}
            />
          ) : (
            <SocialBtn icon={<GoogleLogo />} label="Continuer avec Google" disabled
              onClick={() => setError('Google Client ID non configuré.')} />
          )}

          {/* Facebook */}
          {FACEBOOK_APP_ID ? (
            <SocialBtn icon={<FacebookLogo />} label="Continuer avec Facebook"
              loading={loading === 'facebook'} onClick={loginWithFacebook}
              className="border-[#1877F2]/40 hover:border-[#1877F2]/70" />
          ) : (
            <SocialBtn icon={<FacebookLogo />} label="Continuer avec Facebook" disabled
              onClick={() => setError('Facebook App ID non configuré.')} />
          )}

          {/* Apple */}
          {APPLE_CLIENT_ID ? (
            <SocialBtn icon={<AppleLogo />} label="Continuer avec Apple"
              loading={loading === 'apple'} onClick={loginWithApple}
              className="border-white/10 hover:border-white/25" />
          ) : (
            <SocialBtn icon={<AppleLogo />} label="Continuer avec Apple" disabled
              onClick={() => setError('Apple Client ID non configuré.')} />
          )}
        </div>

        {/* Erreur */}
        {error && (
          <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2 text-center">
            {error}
          </p>
        )}

        {/* Mentions */}
        <p className="text-[11px] text-muted-foreground/70 text-center leading-relaxed">
          En vous connectant, vous acceptez nos{' '}
          <a href="/cgv" target="_blank" className="underline hover:text-muted-foreground">CGV</a> et notre{' '}
          <a href="/confidentialite" target="_blank" className="underline hover:text-muted-foreground">
            politique de confidentialité
          </a>.
        </p>
      </div>
    </div>
  );
}

// ── Bouton social réutilisable ────────────────────────────────────────────────
function SocialBtn({ icon, label, onClick, loading = false, disabled = false, className = '' }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border
        border-border/40 bg-card/40 hover:bg-card/70 text-sm font-medium text-foreground
        transition-all disabled:opacity-40 disabled:cursor-not-allowed ${className}`}>
      <span className="flex-shrink-0">{icon}</span>
      <span className="flex-1 text-left">{label}</span>
      {loading && (
        <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin flex-shrink-0" />
      )}
    </button>
  );
}
